import { useState, useEffect, useRef, useCallback } from "react";
import { aiApi } from "@/api/aiApi";
import type { AIRecord, Record as ChatRecord } from "@/types";
import { useGlobalStore } from "@/store/store";
import eventBus from "@/utils/eventBus";

export function useChatHistory() {
    const token = useGlobalStore((state) => state.token);
    const [chatRecord, setChatRecord] = useState<AIRecord>({ content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } });
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const currentPageRef = useRef(0);

    const fetchHistory = useCallback(async (page: number, size: number, refresh = false) => {
        if (!token || isLoading) return;
        setIsLoading(true);

        try {
            const res = await aiApi.conversation.getHistory({ page, size }) as AIRecord;
            setChatRecord(prev => {
                const existingIds = new Set(prev.content.map(c => c.topicNo));
                const newContent = res.content.filter(c => !existingIds.has(c.topicNo));
                return {
                    content: refresh ? res.content : [...prev.content, ...newContent],
                    page: res.page
                };
            });
            setHasMore(res.page.currentPage < res.page.totalPages - 1);
            currentPageRef.current = res.page.currentPage;
        } catch (error) {
            console.error("Failed to fetch chat history:", error);
        } finally {
            setIsLoading(false);
        }
    }, [token, isLoading]);

    const loadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            fetchHistory(currentPageRef.current + 1, 40);
        }
    }, [hasMore, isLoading, fetchHistory]);

    const refreshHistory = useCallback(() => {
        currentPageRef.current = 0;
        fetchHistory(0, 40, true);
    }, [fetchHistory]);

    const deleteTopic = async (topicNo: string) => {
        try {
            await aiApi.conversation.getDelete([topicNo]);
            setChatRecord(prev => ({
                ...prev,
                content: prev.content.filter(record => record.topicNo !== topicNo)
            }));
            return true;
        } catch (error) {
            console.error("Failed to delete topic:", error);
            return false;
        }
    };

    const updateTopicName = (topicNo: string, newName: string) => {
        setChatRecord(prev => ({
            ...prev,
            content: prev.content.map(record => 
                record.topicNo === topicNo ? { ...record, topicName: newName } : record
            )
        }));
    };

    useEffect(() => {
        if (token) {
            refreshHistory();
        }
    }, [token]);

    useEffect(() => {
        eventBus.on('addHistory', refreshHistory);
        eventBus.on('changeTitleSuccess', (title: string, topicNo: string) => {
            if (topicNo) {
                updateTopicName(topicNo, title);
            }
        });

        return () => {
            eventBus.off('addHistory', refreshHistory);
            eventBus.off('changeTitleSuccess');
        };
    }, [refreshHistory]);

    return { chatRecord, isLoading, hasMore, loadMore, refreshHistory, deleteTopic, updateTopicName };
}

